/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListSlackDirectoryMembersRequest,
  ListSlackDirectoryMembersSecurity,
  MappingStatus,
} from "../models/operations/listslackdirectorymembers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSlackDirectoryMembersQuery,
  prefetchSlackDirectoryMembers,
  queryKeySlackDirectoryMembers,
  SlackDirectoryMembersQueryData,
} from "./slackDirectoryMembers.core.js";
export {
  buildSlackDirectoryMembersQuery,
  prefetchSlackDirectoryMembers,
  queryKeySlackDirectoryMembers,
  type SlackDirectoryMembersQueryData,
};

export type SlackDirectoryMembersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listMembers slackDirectoryConnections
 *
 * @remarks
 * Read observed Slack members across the organization or within one workspace. Does not create identity mappings.
 */
export function useSlackDirectoryMembers(
  request?: ListSlackDirectoryMembersRequest | undefined,
  security?: ListSlackDirectoryMembersSecurity | undefined,
  options?: QueryHookOptions<
    SlackDirectoryMembersQueryData,
    SlackDirectoryMembersQueryError
  >,
): UseQueryResult<
  SlackDirectoryMembersQueryData,
  SlackDirectoryMembersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildSlackDirectoryMembersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listMembers slackDirectoryConnections
 *
 * @remarks
 * Read observed Slack members across the organization or within one workspace. Does not create identity mappings.
 */
export function useSlackDirectoryMembersSuspense(
  request?: ListSlackDirectoryMembersRequest | undefined,
  security?: ListSlackDirectoryMembersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    SlackDirectoryMembersQueryData,
    SlackDirectoryMembersQueryError
  >,
): UseSuspenseQueryResult<
  SlackDirectoryMembersQueryData,
  SlackDirectoryMembersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSlackDirectoryMembersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setSlackDirectoryMembersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      connectionId?: string | undefined;
      search?: string | undefined;
      mappingStatus?: MappingStatus | undefined;
      includeDeactivated?: boolean | undefined;
      includeBots?: boolean | undefined;
      includeGuests?: boolean | undefined;
      sortAsOf?: Date | undefined;
      page?: number | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: SlackDirectoryMembersQueryData,
): SlackDirectoryMembersQueryData | undefined {
  const key = queryKeySlackDirectoryMembers(...queryKeyBase);

  return client.setQueryData<SlackDirectoryMembersQueryData>(key, data);
}

export function invalidateSlackDirectoryMembers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      connectionId?: string | undefined;
      search?: string | undefined;
      mappingStatus?: MappingStatus | undefined;
      includeDeactivated?: boolean | undefined;
      includeBots?: boolean | undefined;
      includeGuests?: boolean | undefined;
      sortAsOf?: Date | undefined;
      page?: number | undefined;
      limit?: number | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "slackDirectoryConnections",
      "listMembers",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllSlackDirectoryMembers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "slackDirectoryConnections", "listMembers"],
  });
}
